import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Chip,
  Stack,
  TextField,
  FormHelperText,
} from '@mui/material';
import {
  Appointment,
  AppointmentStatus,
  Mechanic,
  Task,
  TaskStatus,
} from '../../../types';
import { Add } from '@mui/icons-material';

interface EditAppointmentDialogProps {
  dialogOpen: boolean;
  selectedAppointment: Appointment | null;
  appointmentTasks: Task[];
  mechanics: Mechanic[];
  formatAppointmentDateTime: (appointment: Appointment) => string;
  handleDialogClose: () => void;
  handleStatusChange: (event: SelectChangeEvent) => void;
  handleMechanicChange: (event: SelectChangeEvent) => void;
  /** Plate and bay edits, merged into the appointment being edited. */
  handleDetailsChange: (patch: Partial<Appointment>) => void;
  /** The shop's bays; 0 until they are set up in shop settings. */
  bayCount: number;
  handleNotifyTask: (taskId: string) => void;
  handleSaveChanges: () => void;
  setTaskDialogOpen: (open: boolean) => void;
}

/** Edit an appointment's status, mechanic, plate and bay, and see its tasks. */
const EditAppointmentDialog: React.FC<EditAppointmentDialogProps> = ({
  dialogOpen,
  selectedAppointment,
  appointmentTasks,
  mechanics,
  formatAppointmentDateTime,
  handleDialogClose,
  handleStatusChange,
  handleMechanicChange,
  handleDetailsChange,
  bayCount,
  handleNotifyTask,
  handleSaveChanges,
  setTaskDialogOpen,
}) => {
  return (
    <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Appointment Details</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 'calc(80vh - 150px)', overflowY: 'auto', pb: 3 }}>
        {selectedAppointment && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Customer: {selectedAppointment.customerName}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Date: {formatAppointmentDateTime(selectedAppointment)}
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                value={selectedAppointment.status}
                label="Status"
                onChange={handleStatusChange}
              >
                <MenuItem value={AppointmentStatus.PENDING}>Pending</MenuItem>
                <MenuItem value={AppointmentStatus.IN_PROGRESS}>In Progress</MenuItem>
                <MenuItem value={AppointmentStatus.CONFIRMED}>Confirmed</MenuItem>
                <MenuItem value={AppointmentStatus.COMPLETED}>Completed</MenuItem>
                <MenuItem value={AppointmentStatus.READY_FOR_PICKUP}>Ready for Pickup</MenuItem>
                <MenuItem value={AppointmentStatus.CLOSED_OUT}>Closed Out</MenuItem>
                <MenuItem value={AppointmentStatus.CANCELLED}>Canceled</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="mechanic-select-label">Assign Mechanic</InputLabel>
              <Select
                labelId="mechanic-select-label"
                value={selectedAppointment.mechanicId || ''}
                label="Assign Mechanic"
                onChange={handleMechanicChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {mechanics
                  .filter(mechanic => mechanic.shopId === selectedAppointment.shopId)
                  .map((mechanic) => (
                    <MenuItem key={mechanic.id} value={mechanic.id}>
                      {mechanic.user?.name} ({mechanic.specialization})
                    </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                label="License plate"
                value={selectedAppointment.licensePlate ?? ''}
                onChange={(event) => handleDetailsChange({ licensePlate: event.target.value })}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 12, autoCapitalize: 'characters' } }}
              />
              <FormControl fullWidth disabled={bayCount === 0 && !selectedAppointment.bay}>
                <InputLabel id="bay-select-label">Bay</InputLabel>
                <Select
                  labelId="bay-select-label"
                  value={selectedAppointment.bay ? String(selectedAppointment.bay) : ''}
                  label="Bay"
                  onChange={(event) =>
                    handleDetailsChange({ bay: event.target.value ? Number(event.target.value) : null })
                  }
                >
                  <MenuItem value="">
                    <em>Not in a bay</em>
                  </MenuItem>
                  {Array.from(
                    { length: Math.max(bayCount, selectedAppointment.bay ?? 0) },
                    (_, i) => i + 1,
                  ).map((bay) => (
                    <MenuItem key={bay} value={String(bay)}>
                      Bay {bay}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {bayCount === 0
                    ? 'Set up bays in shop settings first'
                    : 'Held while the job is in progress or completed'}
                </FormHelperText>
              </FormControl>
            </Stack>

            {/* Tasks Section - More Prominent */}
            <Box 
              sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: '2px solid', 
                borderColor: 'divider',
                backgroundColor: 'action.hover',
                borderRadius: 1,
                p: 2
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Service Tasks
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setTaskDialogOpen(true)}
                  startIcon={<Add />}
                >
                  Manage Tasks
                </Button>
              </Box>
              {appointmentTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No tasks found for this appointment. Click "Manage Tasks" to add tasks.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                    {appointmentTasks.map((task) => (
                    <Box
                      key={task.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        mb: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {task.title}
                        </Typography>
                        {task.description && (
                          <Typography variant="body2" color="text.secondary">
                            {task.description}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={task.status.replace('_', ' ')}
                          color={
                            task.status === TaskStatus.COMPLETED ? 'success' :
                            task.status === TaskStatus.IN_PROGRESS ? 'primary' :
                            task.status === TaskStatus.CANCELLED ? 'error' : 'default'
                          }
                          size="small"
                          variant="outlined"
                        />
                        <Button
                          size="small"
                          variant="text"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleNotifyTask(task.id);
                          }}
                        >
                          Notify Customer
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>Cancel</Button>
        <Button onClick={handleSaveChanges} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAppointmentDialog;
